import { useState, useEffect } from "react";
import { Modal, Form, Input, message, Button, Space, Image, Upload, Checkbox, Alert } from "antd";
import { UploadOutlined, DeleteOutlined, PictureOutlined } from "@ant-design/icons";
import axios from "axios";
import { useAuth0 } from "../../react-auth0-spa";
import { useAppContext } from "../../context";
import config from "../../api_config.json";
import updateCategoryMutation from "./queries/updateCategoryMutation";
import CDNFilePicker from "../CDNBrowser/CDNFilePicker";

const EditCategoryModal = ({ visible, onCancel, onUpdate, category }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [iconUrl, setIconUrl] = useState(null);
  const [coverUrl, setCoverUrl] = useState(null);
  const [iconPickerVisible, setIconPickerVisible] = useState(false);
  const [coverPickerVisible, setCoverPickerVisible] = useState(false);
  const [uploading, setUploading] = useState(false);

  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";

  // Initialize form when category changes
  useEffect(() => {
    if (category && visible) {
      form.setFieldsValue({
        name: category.name,
        b2b: category.b2b || false,
        b2c: category.b2c || false,
      });
      setIconUrl(category.icon?.url || null);
      setCoverUrl(category.cover?.url || null);
    }
  }, [category, visible, form]);

  const handleUpdate = async () => {
    try {
      const values = await form.validateFields();

      // Validate at least one of b2b or b2c is selected
      if (!values.b2b && !values.b2c) {
        message.error("Seleziona almeno una modalità (B2B o B2C)");
        return;
      }

      setLoading(true);

      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

      const fields = {
        name: values.name,
        b2b: values.b2b || false,
        b2c: values.b2c || false,
        ACL: {
          public: {
            read: true,
            write: false
          }
        }
      };

      // Add icon if it exists
      if (iconUrl) {
        // Extract filename from URL
        const iconFilename = iconUrl.split('/').pop();
        fields.icon = {
          file: {
            name: iconFilename,
            url: iconUrl,
          }
        };
      }

      // Add cover if it exists
      if (coverUrl) {
        // Extract filename from URL
        const coverFilename = coverUrl.split('/').pop();
        fields.cover = {
          file: {
            name: coverFilename,
            url: coverUrl,
          }
        };
      }

      await axios.post(
        graphqlUrl,
        {
          query: updateCategoryMutation,
          variables: {
            id: category.objectId,
            fields
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      message.success("Categoria aggiornata con successo");
      onUpdate();
    } catch (error) {
      if (error.errorFields) {
        // Validation error from form
        return;
      }
      console.error("Error updating category:", error);
      message.error("Errore durante l'aggiornamento della categoria");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setIconUrl(null);
    setCoverUrl(null);
    onCancel();
  };

  const handleIconSelect = (url) => {
    setIconUrl(url);
    setIconPickerVisible(false);
    message.success("Icona selezionata");
  };

  const handleCoverSelect = (url) => {
    setCoverUrl(url);
    setCoverPickerVisible(false);
    message.success("Copertina selezionata");
  };

  const handleFileUpload = async (file, type) => {
    setUploading(true);
    try {
      const token = await getTokenSilently();
      const formData = new FormData();
      formData.append('file', file);

      // Upload to CDN
      const uploadUrl = `${config.baseUrl[env]}/cdn-upload`;
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      const fileUrl = `${config.cdn[env]}/${data.data.key}`;

      if (type === 'icon') {
        setIconUrl(fileUrl);
        message.success("Icona caricata con successo");
      } else {
        setCoverUrl(fileUrl);
        message.success("Copertina caricata con successo");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      message.error("Errore durante il caricamento del file");
    } finally {
      setUploading(false);
    }
    return false; // Prevent default upload behavior
  };

  return (
    <>
      <Modal
        title="Modifica Categoria"
        open={visible}
        onOk={handleUpdate}
        onCancel={handleCancel}
        okText="Salva"
        cancelText="Annulla"
        confirmLoading={loading}
        width={700}
        maskClosable={false}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            label="Nome"
            name="name"
            rules={[{ required: true, message: "Il nome è obbligatorio" }]}
          >
            <Input placeholder="Nome della categoria" />
          </Form.Item>

          <Form.Item label="Modalità" required>
            <Alert
              message="Seleziona almeno una modalità"
              description="La categoria deve essere disponibile per almeno una modalità di vendita"
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
            />
            <Form.Item name="b2b" valuePropName="checked" noStyle>
              <Checkbox>B2B (Fornitore per agenzie)</Checkbox>
            </Form.Item>
            <br />
            <Form.Item name="b2c" valuePropName="checked" noStyle>
              <Checkbox>B2C (Vendita diretta al cliente)</Checkbox>
            </Form.Item>
          </Form.Item>

          <Form.Item label="Icona">
            <Space direction="vertical" style={{ width: "100%" }}>
              {iconUrl && (
                <div style={{
                  padding: "8px",
                  border: "1px solid #d9d9d9",
                  borderRadius: "4px",
                  display: "inline-block"
                }}>
                  <Space>
                    <Image
                      src={iconUrl}
                      alt="Icon"
                      width={60}
                      height={60}
                      style={{ objectFit: "cover" }}
                    />
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => setIconUrl(null)}
                      size="small"
                    >
                      Rimuovi
                    </Button>
                  </Space>
                </div>
              )}
              <Space>
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => handleFileUpload(file, 'icon')}
                >
                  <Button
                    icon={<UploadOutlined />}
                    loading={uploading}
                  >
                    Carica Immagine
                  </Button>
                </Upload>
                <Button
                  icon={<PictureOutlined />}
                  onClick={() => setIconPickerVisible(true)}
                >
                  Scegli da CDN
                </Button>
              </Space>
            </Space>
          </Form.Item>

          <Form.Item label="Copertina">
            <Space direction="vertical" style={{ width: "100%" }}>
              {coverUrl && (
                <div style={{
                  padding: "8px",
                  border: "1px solid #d9d9d9",
                  borderRadius: "4px",
                  display: "inline-block"
                }}>
                  <Space>
                    <Image
                      src={coverUrl}
                      alt="Cover"
                      width={120}
                      height={80}
                      style={{ objectFit: "cover" }}
                    />
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => setCoverUrl(null)}
                      size="small"
                    >
                      Rimuovi
                    </Button>
                  </Space>
                </div>
              )}
              <Space>
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={(file) => handleFileUpload(file, 'cover')}
                >
                  <Button
                    icon={<UploadOutlined />}
                    loading={uploading}
                  >
                    Carica Immagine
                  </Button>
                </Upload>
                <Button
                  icon={<PictureOutlined />}
                  onClick={() => setCoverPickerVisible(true)}
                >
                  Scegli da CDN
                </Button>
              </Space>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <CDNFilePicker
        visible={iconPickerVisible}
        onSelect={handleIconSelect}
        onCancel={() => setIconPickerVisible(false)}
        basePath=""
        fileTypes={['image']}
      />

      <CDNFilePicker
        visible={coverPickerVisible}
        onSelect={handleCoverSelect}
        onCancel={() => setCoverPickerVisible(false)}
        basePath=""
        fileTypes={['image']}
      />
    </>
  );
};

export default EditCategoryModal;
